/**
 * Lazy Laravel Echo singleton.
 *
 * bootstrap.js used to do `window.Echo = new Echo(...)` unconditionally, which
 * runs on every single page load (this is a multi-page Blade app, not an SPA -
 * app.js/bootstrap.js is the one Mix entry point included on every page). That
 * opened a Reverb WebSocket on pages that never use realtime at all
 * (/inventory, /print_invoice, /app, ...), multiplying a single Reverb blip
 * into a reconnect storm across every open tab company-wide, since pusher-js's
 * default reconnect is a flat 1s retry with no backoff (see
 * node_modules/pusher-js/src/core/connection/connection_manager.ts `closed`/
 * `backoff` handlers - both call `retryIn(1000)`).
 *
 * getEcho() only constructs the connection the first time a component that
 * actually needs realtime calls it, and reuses that same instance for the
 * rest of the page's lifetime.
 */
import Echo from 'laravel-echo';

let echoInstance = null;

export function getEcho() {
  if (echoInstance) return echoInstance;

  window.Pusher = require('pusher-js');

  echoInstance = new Echo({
    broadcaster: 'reverb',
    key: process.env.MIX_REVERB_APP_KEY,
    wsHost: process.env.MIX_REVERB_HOST ?? window.location.hostname,
    wsPort: process.env.MIX_REVERB_PORT ?? 80,
    wssPort: process.env.MIX_REVERB_PORT ?? 443,
    forceTLS: (process.env.MIX_REVERB_SCHEME ?? 'https') === 'https',
    enabledTransports: ['ws', 'wss'],
    // Private channel auth goes through window.axios so it carries the same
    // session cookie + CSRF header the rest of the app uses (pusher-js's
    // built-in XHR authorizer does not send either by default).
    authorizer: (channel) => ({
      authorize: (socketId, callback) => {
        window.axios.post('/broadcasting/auth', {
          socket_id: socketId,
          channel_name: channel.name,
        }).then(response => {
          callback(false, response.data);
        }).catch(error => {
          callback(true, error);
        });
      },
    }),
  });

  window.Echo = echoInstance;
  return echoInstance;
}
